import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import {
  LayoutDashboard,
  Users,
  Tv,
  FolderTree,
  Package,
  Film,
  Calendar,
  Trophy,
  Settings,
  Settings2,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  Download,
  AlertTriangle,
  Youtube,
  Radio,
  Bug,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'

// Check if running in development/local environment by hostname
const isDev = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname.startsWith('192.168.') ||
  window.location.hostname.startsWith('10.') ||
  window.location.port === '3025' ||
  window.location.port === '3026'
)

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Streams', href: '/streams', icon: Tv },
  { name: 'Categories', href: '/categories', icon: FolderTree },
  { name: 'Bouquets', href: '/bouquets', icon: Package },
  { name: 'Series', href: '/series', icon: Film },
  { name: 'YouTube', href: '/youtube', icon: Youtube },
  { name: 'Pluto TV', href: '/plutotv', icon: Radio },
  { name: 'EPG', href: '/epg', icon: Calendar },
  { name: 'Sports EPG', href: '/sports-epg', icon: Trophy },
  { name: 'Import', href: '/import', icon: Download },
  { name: 'Transcoding', href: '/transcoding', icon: Settings2 },
  { name: 'Device Logs', href: '/device-logs', icon: Bug },
  { name: 'Settings', href: '/settings', icon: Settings },
]

export default function Layout() {
  const { logout, user } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme')
      if (stored) {
        return stored === 'dark'
      }
      // Default to dark mode if no preference stored
      return true
    }
    return true
  })

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }, [isDark])

  // In dev mode, change the favicon to red and update title
  useEffect(() => {
    if (isDev) {
      // Update title
      document.title = '🔴 DEV - OBA Admin Panel'

      // Create a red favicon dynamically
      const canvas = document.createElement('canvas')
      canvas.width = 32
      canvas.height = 32
      const ctx = canvas.getContext('2d')
      if (ctx) {
        // Red circle background
        ctx.fillStyle = '#dc2626'
        ctx.beginPath()
        ctx.arc(16, 16, 15, 0, Math.PI * 2)
        ctx.fill()

        // White "D" for Dev
        ctx.fillStyle = '#ffffff'
        ctx.font = 'bold 20px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('D', 16, 17)

        // Set as favicon
        const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link')
        link.type = 'image/png'
        link.rel = 'icon'
        link.href = canvas.toDataURL()
        document.head.appendChild(link)
      }
    }
  }, [])

  const toggleTheme = () => setIsDark(!isDark)

  return (
    <div className="min-h-screen bg-background">
      {/* Dev environment banner */}
      {isDev && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white py-1 px-4 flex items-center justify-center gap-2 text-sm font-bold shadow-lg">
          <AlertTriangle className="w-4 h-4" />
          <span>DEVELOPMENT ENVIRONMENT</span>
          <AlertTriangle className="w-4 h-4" />
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-sidebar transform transition-transform duration-200 lg:translate-x-0',
          'flex flex-col border-r border-sidebar-muted',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          isDev && 'pt-8' // Account for dev banner
        )}
      >
        {/* Logo section */}
        <div className="flex items-center justify-center h-32 px-4 relative">
          <Link to="/" className="flex items-center">
            <img src="/logo.png" alt="OBA" className="h-28 w-auto" />
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="absolute right-4 p-2 rounded-lg text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-muted lg:hidden transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-muted'
                )}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.name}</span>
              </Link>
            )
          })}
        </nav>

        {/* User section */}
        <div className="px-4 py-4 border-t border-sidebar-muted">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-sidebar-muted text-sidebar-foreground font-medium">
              {user?.username?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-medium text-sidebar-foreground truncate">
                {user?.username || 'Admin'}
              </p>
              <p className="text-sm text-sidebar-foreground/60 truncate">
                {user?.role || 'Administrator'}
              </p>
            </div>
            <button
              onClick={() => logout()}
              className="p-2 rounded-lg text-sidebar-foreground/60 hover:text-destructive hover:bg-sidebar-muted transition-colors"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn('lg:pl-64 min-h-screen flex flex-col', isDev && 'pt-8')}>
        {/* Top bar */}
        <header className={cn(
          'sticky z-30 flex items-center justify-between h-16 px-6 bg-background border-b border-border',
          isDev ? 'top-8' : 'top-0'
        )}>
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors lg:hidden"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="hidden lg:block" />

          {/* Right side actions */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            title={isDark ? 'Light mode' : 'Dark mode'}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
